export const AttackOutcome = {
  InKill: 'InKill',
  ToolKill: 'ToolKill',
  Out: 'Out',
  Dug: 'Dug',
  BlockKill: 'BlockKill',
  BlockRecycle: 'BlockRecycle',
  Fault: 'Fault',
  FreeBall: 'FreeBall',
  BallInNet: 'BallInNet',
} as const
export type AttackOutcome = typeof AttackOutcome[keyof typeof AttackOutcome]

export const AttackOriginLocationHorizontal = {
  One: 'One',
  Two: 'Two',
  Three: 'Three',
  Four: 'Four',
  Five: 'Five',
} as const
export type AttackOriginLocationHorizontal = typeof AttackOriginLocationHorizontal[keyof typeof AttackOriginLocationHorizontal]

export const AttackOriginLocationVertical = {
  On: 'On',
  Off: 'Off'
} as const
export type AttackOriginLocationVertical = typeof AttackOriginLocationVertical[keyof typeof AttackOriginLocationVertical]

export type AttackOriginLocation = {
  horizontal: AttackOriginLocationHorizontal;
  vertical: AttackOriginLocationVertical;
}

export const originLocationMatches = (location1?: AttackOriginLocation, location2?: AttackOriginLocation): boolean => {
  if(!location1 && !location2) return true;
  if(!location1 || !location2) return false;
  return location1.horizontal === location2.horizontal && location1.vertical === location2.vertical;
}

// export const ShotLocation = {
//   ShallowOne: 'ShallowOne',
//   MiddleOne: 'MiddleOne',
//   DeepOne: 'DeepOne',
//   ShallowTwo: 'ShallowTwo',
//   MiddleTwo: 'MiddleTwo',
//   DeepTwo: 'DeepTwo',
//   ShallowThree: 'ShallowThree',
//   MiddleThree: 'MiddleThree',
//   DeepThree: 'DeepThree',
//   ShallowFour: 'ShallowFour',
//   MiddleFour: 'MiddleFour',
//   DeepFour: 'DeepFour',
//   ShallowFive: 'ShallowFive',
//   MiddleFive: 'MiddleFive',
//   DeepFive: 'DeepFive',
//   Tool: 'Tool'
// } as const
// export type ShotLocation = typeof ShotLocation[keyof typeof ShotLocation]

export const AttackDestinationLocationHorizontal = {
  One: 'One',
  Two: 'Two',
  Three: 'Three',
  Four: 'Four',
  Five: 'Five',
} as const
export type AttackDestinationLocationHorizontal = typeof AttackDestinationLocationHorizontal[keyof typeof AttackDestinationLocationHorizontal]

export const AttackDestinationLocationVertical = {
  Shallow: 'Shallow',
  Middle: 'Middle',
  Deep: 'Deep',
} as const
export type AttackDestinationLocationVertical = typeof AttackDestinationLocationVertical[keyof typeof AttackDestinationLocationVertical]

export type AttackDestinationLocation = {
  horizontal: AttackDestinationLocationHorizontal;
  vertical: AttackDestinationLocationVertical;
}
export const destinationLocationMatches = (location1?: AttackDestinationLocation, location2?: AttackDestinationLocation): boolean => {
  if(!location1 && !location2) return true;
  if(!location1 || !location2) return false;
  return location1.horizontal === location2.horizontal && location1.vertical === location2.vertical;
}

export const AttackOutLocation = {
  WideLeftShallow: 'WideLeftShallow',
  WideLeftDeep: 'WideLeftDeep',
  WideRightShallow: 'WideRightShallow',
  WideRightDeep: 'WideRightDeep',
  DeepLeft: 'DeepLeft',
  DeepRight: 'DeepRight',
  Net: 'Net'
} as const
export type AttackOutLocation = typeof AttackOutLocation[keyof typeof AttackOutLocation]

export const AttackType = {
  Swing: 'Swing',
  Shot: 'Shot'
} as const
export type AttackType = typeof AttackType[keyof typeof AttackType]

export const PassType = {
  InSystem: 'InSystem',
  OutOfSystem: 'OutOfSystem'
} as const
export type PassType = typeof PassType[keyof typeof PassType]

export interface BasicAttackInfo {
  attackingPlayerId: number;
  passType: PassType;
  isOnTwo: boolean;
}

export interface AttackTypeInfo {
  attackType: AttackType;
}

export interface AttackOriginLocationInfo {
  originLocation: AttackOriginLocation;
}

// export interface AttackLocationInfo {
//   attackOrigin: AttackOriginLocation;
//   attackSubLocation: AttackSubLocation;
// }

export type AttackWithoutDestinationLocation = BasicAttackInfo & AttackOriginLocationInfo & AttackTypeInfo & {
  outcome: 'BlockKill' | 'BlockRecycle' | 'ToolKill' | 'BallInNet';
}

export type AttackWithDestinationLocation = BasicAttackInfo & AttackOriginLocationInfo & AttackTypeInfo & {
  outcome: 'InKill' | 'Dug';
  destinationLocation: AttackDestinationLocation;
}

export type AttackWithOutLocation = BasicAttackInfo & AttackOriginLocationInfo & AttackTypeInfo & {
  outcome: 'Out';
  outLocation: AttackOutLocation;
}

export type AttackWithoutAttackType = BasicAttackInfo & AttackOriginLocationInfo & {
  outcome: 'Fault';
}

export type AttackWithoutAttackTypeOrOriginLocation = BasicAttackInfo & {
  outcome: 'FreeBall';
}

export type AttackInfo = BasicAttackInfo & (
  AttackWithoutDestinationLocation |
  AttackWithDestinationLocation |
  AttackWithOutLocation |
  AttackWithoutAttackType |
  AttackWithoutAttackTypeOrOriginLocation)

export const isAttackWithoutDestinationLocation = (attackInfo: AttackInfo): attackInfo is AttackWithoutDestinationLocation => {
  return attackInfo.outcome === 'BlockKill' || attackInfo.outcome === 'BlockRecycle' || attackInfo.outcome === 'ToolKill' || attackInfo.outcome === 'BallInNet';
}

export const isAttackWithDestinationLocation = (attackInfo: AttackInfo): attackInfo is AttackWithDestinationLocation => {
  return attackInfo.outcome === 'InKill' || attackInfo.outcome === 'Dug';
}

export const isAttackWithOutLocation = (attackInfo: AttackInfo): attackInfo is AttackWithOutLocation => {
  return attackInfo.outcome === 'Out';
}

export const isAttackWithoutAttackType = (attackInfo: AttackInfo): attackInfo is AttackWithoutAttackType => {
  return attackInfo.outcome === 'Fault';
}

export const isAttackWithoutAttackTypeOrOriginLocation = (attackInfo: AttackInfo): attackInfo is AttackWithoutAttackTypeOrOriginLocation => {
  return attackInfo.outcome === 'FreeBall';
}

export const isAttackWithAttackType = (attackInfo: AttackInfo): attackInfo is (AttackInfo & AttackTypeInfo) => {
  return (attackInfo as AttackTypeInfo).attackType !== undefined;
}

export const isAttackWithOriginLocation = (attackInfo: AttackInfo): attackInfo is (AttackInfo & AttackOriginLocationInfo) => {
  return (attackInfo as AttackOriginLocationInfo).originLocation !== undefined;
}
